import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { ORDER_STATUSES } from '../firebase/schema';

const getDeliveryDate = () => {
  let date = new Date();
  let businessDays = 6;
  while (businessDays > 0) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      businessDays--;
    }
  }
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

function QuickOrderCheckout() {
  const location = useLocation();
  const navigate = useNavigate();
  const orderData = location.state?.orderData;
  
  // Move useState before any conditional returns
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    expiry: '',
    cvv: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  // Redirect if no order data
  if (!orderData) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#1D1D1D',
        padding: '2rem',
        color: '#FFFFFF',
        fontFamily: 'Montserrat, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ marginBottom: '1rem' }}>
          No order data found. Please start your order from the beginning.
        </div>
        <button
          onClick={() => navigate('/quick-order')}
          style={{
            backgroundColor: '#98F9B3',
            color: '#000000',
            padding: '0.75rem 1.5rem',
            borderRadius: '0',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          Start New Order
        </button>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create the main order
      const orderRef = await addDoc(collection(db, 'orders'), {
        customerName: paymentInfo.name,
        projectManager: 'Ben',
        status: ORDER_STATUSES.READY_TO_ORDER,
        createdAt: Timestamp.now(),
        dueDate: Timestamp.fromDate(new Date(getDeliveryDate())),
        email: paymentInfo.email,
        phone: paymentInfo.phone,
        totalAmount: orderData.total,
        readyToOrder: true,
        readyToOrderDate: Timestamp.now()
      });

      // Create the order line
      await addDoc(collection(db, 'orderLines'), {
        orderId: orderRef.id,
        totalQuantity: Object.values(orderData.quantities).reduce((sum, qty) => sum + qty, 0),
        status: 'pending',
        design: orderData.designImage,
        pricePerUnit: orderData.pricePerShirt,
        sizes: orderData.quantities
      });

      // Navigate to confirmation page with order details
      navigate('/order-confirmation', {
        state: {
          orderDetails: {
            orderId: orderRef.id,
            total: orderData.total,
            deliveryDate: getDeliveryDate(),
            quantities: orderData.quantities,
            customerName: paymentInfo.name,
            email: paymentInfo.email,
            phone: paymentInfo.phone,
            designImage: orderData.designImage
          }
        }
      });

    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to place order. Please try again.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#1D1D1D',
      padding: '6rem 2rem 2rem 2rem',
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        gap: '2rem'
      }}>
        {/* Order Summary */}
        <div style={{
          flex: '1',
          backgroundColor: '#2D2D2D',
          padding: '1.5rem',
          border: '1px solid #98F9B3',
          borderRadius: '0',
          height: 'fit-content'
        }}>
          <h2 style={{
            color: '#FFFFFF',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '1.25rem',
            marginBottom: '1.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Order Summary
          </h2>
          
          {orderData.designImage && (
            <div style={{
              marginBottom: '1.5rem',
              borderBottom: '1px solid #98F9B3',
              paddingBottom: '1.5rem'
            }}>
              <h3 style={{
                color: '#FFFFFF',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.875rem',
                marginBottom: '1rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Your Design
              </h3>
              <div style={{
                backgroundColor: '#3D3D3D',
                padding: '1rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <img 
                  src={orderData.designImage}
                  alt="Your Design"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>
          )}

          {/* Quantities */}
          <div style={{ marginBottom: '1rem' }}>
            {Object.entries(orderData.quantities).map(([size, quantity]) => (
              quantity > 0 && (
                <div key={size} style={{
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '0.875rem',
                  marginBottom: '0.5rem'
                }}>
                  Size {size}: {quantity}
                </div>
              )
            ))}
          </div>

          {/* Price Details */}
          <div style={{
            borderTop: '1px solid #98F9B3',
            paddingTop: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{
              color: '#FFFFFF',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '0.875rem',
              marginBottom: '0.5rem'
            }}>
              Price per shirt: ${orderData.pricePerShirt}.00
            </div>
            <div style={{
              color: '#FFFFFF',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '1.125rem',
              fontWeight: '500'
            }}>
              Total: ${orderData.total}.00
            </div>
          </div>

          {/* Delivery Info */}
          <div style={{
            color: '#FFFFFF',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '0.875rem',
            borderTop: '1px solid #98F9B3',
            paddingTop: '1rem'
          }}>
            Guaranteed Delivery by {orderData.deliveryDate}
          </div>
        </div>

        {/* Payment Form */}
        <form 
          onSubmit={handleSubmit}
          style={{
            flex: '2',
            backgroundColor: '#2D2D2D',
            padding: '1.5rem',
            border: '1px solid #98F9B3',
            borderRadius: '0'
          }}
        >
          <h2 style={{
            color: '#FFFFFF',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '1.25rem',
            marginBottom: '1.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Contact Information
          </h2>

          {/* Just keep the essential fields */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#FFFFFF',
              fontSize: '0.875rem',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Client Name
            </label>
            <input
              type="text"
              name="client"
              value={paymentInfo.client}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: '#3D3D3D',
                border: '1px solid #98F9B3',
                borderRadius: '0',
                color: '#FFFFFF',
                fontFamily: 'Montserrat, sans-serif'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#FFFFFF',
              fontSize: '0.875rem',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={paymentInfo.email}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: '#3D3D3D',
                border: '1px solid #98F9B3',
                borderRadius: '0',
                color: '#FFFFFF',
                fontFamily: 'Montserrat, sans-serif'
              }}
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#FFFFFF',
              fontSize: '0.875rem',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={paymentInfo.phone}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: '#3D3D3D',
                border: '1px solid #98F9B3',
                borderRadius: '0',
                color: '#FFFFFF',
                fontFamily: 'Montserrat, sans-serif'
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            style={{
              width: '100%',
              backgroundColor: '#98F9B3',
              color: '#000000',
              padding: '0.75rem',
              borderRadius: '0',
              fontWeight: '500',
              marginTop: 'auto',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Complete Purchase
          </button>
        </form>
      </div>
    </div>
  );
}

export default QuickOrderCheckout; 